import { useFormik } from 'formik';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FiChevronLeft, FiInfo } from 'react-icons/fi';
import { useMutation } from 'react-query';
import { Link, useNavigate } from 'react-router-dom';

import {
  Button,
  Dropdown,
  Form,
  InputField,
  InputRadio,
  Modal,
} from '@/components';
import { Layout, Sidebar } from '@/components/GenericsTournaments';
import { acceptTda, inviteTda, photoTda } from '@/components/GenericsTournaments/Icons';
import InputError from '@/components/InputError';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import usePinsAccount from '@/hooks/usePinsAccount';
import { useTdaData } from '@/hooks/useTda';
import useToast from '@/hooks/useToast';
import useTourney from '@/hooks/useTourney';
import ROUTES from '@/router/routes';
import {
  getAvailableNameTDA,
  getAvailablePinsTDAVIP,
  getPinsValidationTDAVIP,
  reactivateTDA,
  registerTDA,
} from '@/services/tda';
import { DateGDT, SelectValue } from '@/types';
import {
  EnumFriendsTournamentType,
  RequestReactivarTorneoAnterior,
  TorneoAnterior,
} from '@/types/tda';
import { EnumContextTourneys } from '@/types/team';
import {
  oneSpaceBetweenWordMinTwoChar,
  alphanumericMaxLength,
  createCommonTdaSchema,
  createPremiumTdaSchema,
  createTdaWithPinsSchema,
  TEXTS,
  validateFieldError,
  cn,
} from '@/utils';
import { SECTION_NAME_FOR_ADS } from '@/utils/ads';

export type RequestCreateTDA = {
  tournamentName: string;
  tournamentType: number;
  tournamentDate: number;
  pin1?: string;
  pin2?: string;
  isPremiumUser: boolean;
};

type FormData = {
  tourneyName: string;
  tourneyType: number;
  initDay: SelectValue;
  pin1: string;
  pin2: string;
};

export const CreateTournamentTDA = ({
  context = EnumContextTourneys.Create,
}: {
  context?: EnumContextTourneys;
}) => {
  const toast = useToast();
  const { isDesktop } = useIsDesktop();
  const { tourneyData, handleSetTourney } = useTourney();
  const navigate = useNavigate();
  const { pinsData } = usePinsAccount();
  const [showModal, setShowModal] = useState<boolean>(false);
  const [showDesktopVideoModal, setShowDesktopVideoModal] =
    useState<boolean>(false);
  const [pinErrorMessage, setPinErrorMessage] = useState<string>('');
  const [tourneyType, setTourneyType] = useState<EnumFriendsTournamentType>(
    EnumFriendsTournamentType.Common
  );

  const isReactivate = useMemo(() => {
    return context === EnumContextTourneys.Reactivate;
  }, [context]);

  const { tdaData, isSuccess } = useTdaData();
  const nextDates = useMemo(() => {
    return isSuccess
      ? tdaData?.fechasDisponibles.map((date: DateGDT) => ({
          label: date.nombre,
          value: !isReactivate ? date.id : date.orden,
        }))
      : [];
  }, [tdaData, isSuccess, isReactivate]);

  const [oldTourneyData, setOldTourneyData] = useState<TorneoAnterior>(
    tourneyData.tourney
  );

  useEffect(() => {
    if (tourneyData.tourney && isReactivate) {
      setOldTourneyData(tourneyData.tourney);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourneyData.tourney, isReactivate]);

  const { mutateAsync: onRequestReactivate } = useMutation(
    (formData: RequestReactivarTorneoAnterior) => {
      const { nombreTA, idTA, ordenFechaInicioTDA, vip, pin1, pin2 } = formData;
      return reactivateTDA({
        nombreTA,
        idTA,
        ordenFechaInicioTDA,
        vip,
        pin1,
        pin2,
      });
    }
  );

  const { mutateAsync: onRequestCreate } = useMutation(
    (formData: RequestCreateTDA) => {
      const {
        tournamentName,
        tournamentType,
        tournamentDate,
        pin1,
        pin2,
        isPremiumUser,
      } = formData;
      return registerTDA({
        tournamentName,
        tournamentType,
        tournamentDate,
        isPremiumUser,
        pin1,
        pin2,
      });
    }
  );

  const onHandleValidationSchema = useCallback(() => {
    if (tourneyType === EnumFriendsTournamentType.Common) {
      return createCommonTdaSchema;
    } else {
      if (pinsData.isPremium) {
        return createPremiumTdaSchema;
      } else {
        return createTdaWithPinsSchema;
      }
    }
  }, [pinsData.isPremium, tourneyType]);

  const {
    touched,
    errors,
    isSubmitting,
    setFieldValue,
    setFieldTouched,
    setFieldError,
    handleSubmit,
    values,
    handleBlur,
    setStatus,
    status,
    isValid,
  } = useFormik({
    validateOnMount: false,
    validateOnBlur: true,
    validationSchema: () => onHandleValidationSchema(),
    initialValues: {
      tourneyType: EnumFriendsTournamentType.Common,
      tourneyName: !isReactivate ? '' : oldTourneyData?.nombre,
      initDay: { label: '', value: -1 },
      pin1: '',
      pin2: '',
    },
    onSubmit: async (
      { tourneyName, tourneyType, initDay, pin1, pin2 }: FormData,
      { setSubmitting }
    ) => {
      if (initDay) {
        if (!status || errors.tourneyName || errors.tourneyType) {
          return;
        }

        if (
          tourneyType === EnumFriendsTournamentType.Vip &&
          !pinsData.isPremium
        ) {
          setPinErrorMessage('');
          const isValidPins = await handlePinValidation(pin1, pin2);
          if (!isValidPins) {
            return;
          }
        }

        const requestCreate = async (data: RequestCreateTDA) => {
          const response = await onRequestCreate(data);

          if (response.estado) {
            handleSetTourney({
              ...tourneyData,
              id: response.idtorneo,
              initDay: values.initDay.value,
              isOwner: true,
              isVip: tourneyType === EnumFriendsTournamentType.Vip,
              tourney: {
                ...tourneyData.tourney,
                nombre: tourneyName,
              },
            });
            setShowModal(true);
          } else {
            toast.show(TEXTS.defaultError);
          }
        };

        const requestReactivate = async (
          data: RequestReactivarTorneoAnterior
        ) => {
          const response = await onRequestReactivate(data);

          if (response.estado) {
            handleSetTourney({
              ...tourneyData,
              id: response?.idTaCreado || 0,
              tourney: {
                ...tourneyData.tourney,
                nombre: tourneyName,
              },
            });
            setShowModal(true);
          } else {
            if (response.nombreSugerido || response.nombreTomado) {
              toast.show(TEXTS.nameExist);
            } else {
              toast.show(TEXTS.defaultError);
            }
          }
        };

        if (!isReactivate) {
          requestCreate({
            tournamentName: tourneyName,
            tournamentType: tourneyType,
            tournamentDate: initDay?.value,
            pin1,
            pin2,
            isPremiumUser: pinsData?.isPremium,
          });
        } else {
          requestReactivate({
            nombreTA: tourneyName,
            vip: tourneyType,
            idTA: oldTourneyData.id,
            ordenFechaInicioTDA: initDay?.value,
            pin1,
            pin2,
          });
        }
      }
      setSubmitting(false);
    },
  });

  // console.log(parseInt(values?.initDay?.label.split(' ')[1]) - 1);

  useEffect(() => {
    if (isReactivate) {
      if (values.tourneyType === EnumFriendsTournamentType.Vip) {
        setStatus(false);
      } else {
        oldTourneyData?.nombre && setStatus(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oldTourneyData?.nombre, values.tourneyType, isReactivate]);

  const handlePinValidation = async (pin1: string, pin2: string) => {
    const response = await getPinsValidationTDAVIP(pin1, pin2);

    if (!response.estado && 'codError' in response) {
      setPinErrorMessage(response.mensaje);
    }

    return response.estado;
  };

  const MIN_CHARACTERS_TO_VALIDATE_NAME = 2;
  const handleNameTournamentValidation = useCallback(
    async (tourneyName: string) => {
      if (tourneyName.trim().length >= MIN_CHARACTERS_TO_VALIDATE_NAME) {
        const response = await getAvailableNameTDA(
          encodeURIComponent(tourneyName)
        );
        if (!response.estado && 'codError' in response) {
          if (response.codError === 302) {
            setFieldError('tourneyName', TEXTS.nameExist);
            setStatus(false);
          } else {
            setFieldError('tourneyName', response.mensaje);
            setStatus(false);
          }
        } else {
          setFieldError('tourneyName', undefined);
          setStatus(true);
        }
      } else {
        setStatus(false);
      }
    },
    [setFieldError, setStatus]
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = oneSpaceBetweenWordMinTwoChar(e.target.value);

    setFieldValue('tourneyName', value);
    setFieldTouched('tourneyName', true, false);

    if (value.trim().length === 0) {
      setFieldError('tourneyName', TEXTS.minLetters);
      setStatus(false);
    } else {
      handleNameTournamentValidation(value);
    }
  };

  const handleInputRadio = (value: number) => {
    setTourneyType(value);
    setFieldValue('tourneyType', value);

    values.tourneyName.trim().length >= MIN_CHARACTERS_TO_VALIDATE_NAME &&
      handleNameTournamentValidation(values.tourneyName);
  };

  // simulo instancia init para manejo de input radio y validaciones
  useEffect(() => {
    handleInputRadio(EnumFriendsTournamentType.Common);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelect = useCallback(
    (e: SelectValue) => {
      setFieldValue('initDay', e);
      values.tourneyName?.trim().length >= MIN_CHARACTERS_TO_VALIDATE_NAME &&
        handleNameTournamentValidation(values.tourneyName);
    },
    [handleNameTournamentValidation, setFieldValue, values.tourneyName]
  );

  const options = useMemo(() => {
    if (isSuccess && nextDates) {
      handleSelect(nextDates[0]);
      return nextDates || [];
    }
    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextDates, isSuccess]);

  const fetchAndSetPins = useCallback(async () => {
    setPinErrorMessage('');
    const response = await getAvailablePinsTDAVIP();

    if (response.estado) {
      if (response.pin1 !== undefined) {
        setFieldValue('pin1', response.pin1);
      }
      if (response.pin2 !== undefined) {
        setFieldValue('pin2', response.pin2);
      }
      setTimeout(() => {
        setFieldError('pin1', undefined);
        setFieldError('pin2', undefined);
        setStatus(true);
      }, 0);
    }
  }, [setFieldError, setFieldValue, setStatus]);

  useEffect(() => {
    if (
      values.tourneyType === EnumFriendsTournamentType.Vip &&
      !pinsData.isPremium
    ) {
      fetchAndSetPins();
    }
  }, [values.tourneyType, pinsData.isPremium, fetchAndSetPins]);

  const isSubmitable = useMemo(() => {
    const isVip =
      !pinsData.isPremium &&
      values.tourneyType === EnumFriendsTournamentType.Vip;
    const errorsPins = isVip
      ? errors.pin1 ||
        errors.pin2 ||
        values.pin1.length < 2 ||
        values.pin2.length < 2
      : false;
    return (
      isValid &&
      status &&
      !errors.tourneyName &&
      values.tourneyName.trim().length >= MIN_CHARACTERS_TO_VALIDATE_NAME &&
      !isSubmitting &&
      !errorsPins
    );
  }, [
    pinsData.isPremium,
    values.tourneyType,
    values.pin1.length,
    values.pin2.length,
    values.tourneyName,
    errors.pin1,
    errors.pin2,
    errors.tourneyName,
    isValid,
    status,
    isSubmitting,
  ]);

  const textDefault = useMemo(() => {
    if (!isReactivate) {
      return {
        title: 'Crear nuevo torneo',
      };
    } else {
      return {
        title: 'Reactivar torneo',
      };
    }
  }, [isReactivate]);

  const ModalUI = useCallback(() => {
    if (!isReactivate) {
      return (
        <p className="text-base font-semibold mb-[30px] text-center">
          ¡Felicitaciones! El torneo fue creado con éxito.
          <br />
          ¿Querés invitar a tus amigos ahora?
        </p>
      );
    } else {
      return (
        <p className="mb-[30px] text-center">
          {!pinsData.isPremium ? (
            <>
              <strong className="text-black text-base">
                ¡Felicitaciones! El torneo fue reactivado con éxito.
              </strong>
              <span className="block mt-2 text-sm font-medium">
                Se envió un mail a todos tus amigos que participaron
                invitándolos nuevamente.
                <br />
                ¿Querés invitar más amigos ahora?
              </span>
            </>
          ) : (
            <>
              <strong className="text-black text-base">
                ¡Felicitaciones! El torneo fue reactivado con éxito.
              </strong>
              <span className="block mt-2 text-sm font-medium">
                Recordá que por tener activo tu Pack Premium podés crear la
                cantidad de torneos VIP que prefieras.
                <br />
                <br />
                Se envió un mail a todos tus amigos que participaron
                invitándolos nuevamente.
                <br />
                ¿Querés invitar más amigos ahora?
              </span>
            </>
          )}
        </p>
      );
    }
  }, [isReactivate, pinsData.isPremium]);

  return (
    <Layout
      actionFooter={{
        actionHandler: handleSubmit,
        buttonName: textDefault.title,
        buttonDisabled: !isSubmitable,
      }}
      adsSections={SECTION_NAME_FOR_ADS.torneodeamigos}
      className="px-4 lg:w-[80%]"
      main={() => (
        <div className="sm:mt-[30px] lg:mt-0 lg:w-[400px]">
          {isDesktop && (
            <>
              <Link
                className="text-gdt-blue font-bold relative left-[-20px]"
                to="/torneo-de-amigos"
              >
                <FiChevronLeft className="text-gdt-blue w-6 h-6 inline" />
                <span className="pl-[4px]">Volver</span>
              </Link>
              <div className="mt-[40px]">
                <span className="font-bold text-base text-gdt-gray lg:text-lg">
                  {textDefault.title}
                </span>
              </div>
            </>
          )}
          <p className="font-medium lg:font-semibold text-sm mt-4 text-gdt-gray-2">
            Si {!isReactivate ? 'creás un Torneo' : 'reactivás tu Torneo como'}{' '}
            VIP vas a poder personalizarlo al máximo y contar con más
            herramientas estadísticas.
          </p>
          <InputRadio.Wrapper className="flex flex-row lg:flex-row gap-4 lg:gap-8 my-[20px]">
            <InputRadio.Input
              isChecked={
                values.tourneyType === EnumFriendsTournamentType.Common
              }
              label="Común"
              name="tourneyType"
              value={values.tourneyType}
              onChange={() =>
                handleInputRadio(EnumFriendsTournamentType.Common)
              }
            />
            <div className="flex flex-row items-center">
              <InputRadio.Input
                isChecked={values.tourneyType === EnumFriendsTournamentType.Vip}
                label={
                  !isReactivate
                    ? 'VIP'.concat(pinsData.isPremium ? '' : ' (2 Pines)')
                    : 'VIP'
                }
                name="tourneyType"
                value={values.tourneyType}
                onChange={() => handleInputRadio(EnumFriendsTournamentType.Vip)}
              />
              {isDesktop && (
                <FiInfo
                  className="text-gdt-blue relative lg:relative lg:right-auto left-[5px] lg:left-0 lg:top-0 -top-[3px] cursor-pointer hover:opacity-90 ml-[10px]"
                  // data-tooltip-id="tooltip-info"
                  size={18}
                  onClick={() => setShowDesktopVideoModal(true)}
                />
              )}
            </div>
          </InputRadio.Wrapper>
          <div className="lg:w-[80%]">
            <Form.Field>
              <div className="mb-[20px]">
                {/* ? 'Este nombre ya existe. Por favor, elegí otro.' */}
                <InputField
                  behavior="default"
                  error={validateFieldError(
                    touched.tourneyName,
                    errors.tourneyName,
                    values.tourneyName.length
                  )}
                  label="Nombre del torneo"
                  maxLength={25}
                  name="tourneyName"
                  placeholder="Ingresar nombre"
                  success={!errors.tourneyName && status}
                  value={values.tourneyName}
                  onBlur={(e) => {
                    if (values.tourneyName.length >= 2) return;
                    handleBlur(e);
                  }}
                  onChange={handleInputChange}
                />
              </div>
            </Form.Field>
            <Form.Field>
              <Dropdown
                label={'Fecha de Inicio del Torneo'}
                options={options}
                selected={values.initDay || null}
                onChange={handleSelect}
              />
            </Form.Field>
            {!pinsData.isPremium &&
              values.tourneyType !== EnumFriendsTournamentType.Common && (
                <div className="mt-[30px]">
                  <Form.Field>
                    <div className="mb-[10px]">
                      <InputField
                        behavior="default"
                        error={validateFieldError(
                          touched.pin1,
                          errors.pin1,
                          values.pin1.length
                        )}
                        label="Ingresá tus dos pines"
                        maxLength={12}
                        name="pin1"
                        placeholder="Ingresar"
                        success={!errors.pin1 && values.pin1.length > 0}
                        type="text"
                        value={values.pin1}
                        onBlur={handleBlur}
                        onChange={(e) => {
                          if (alphanumericMaxLength(e.target.value, 12)) {
                            setFieldValue('pin1', e.target.value.trim());
                          }
                        }}
                      />
                    </div>
                  </Form.Field>
                  <Form.Field>
                    <div>
                      <InputField
                        behavior="default"
                        error={validateFieldError(
                          touched.pin2,
                          errors.pin2,
                          values.pin2.length
                        )}
                        maxLength={12}
                        name="pin2"
                        placeholder="Ingresar"
                        success={!errors.pin2 && values.pin2.length > 0}
                        type="text"
                        value={values.pin2}
                        onBlur={handleBlur}
                        onChange={(e) => {
                          if (alphanumericMaxLength(e.target.value, 12)) {
                            setFieldValue('pin2', e.target.value.trim());
                          }
                        }}
                      />
                    </div>
                  </Form.Field>
                  <div className="m-[5px]">
                    {pinErrorMessage && (
                      <InputError.Label id={'pinErrorID'}>
                        {pinErrorMessage}
                      </InputError.Label>
                    )}
                  </div>
                  <div
                    hidden={values.pin1.length > 0 && values.pin2.length > 0}
                  >
                    <Link
                      className="flex text-gdt-blue font-bold justify-center mt-[20px] underline"
                      to={ROUTES.myAccount}
                    >
                      Obtener Pines
                    </Link>
                  </div>
                </div>
              )}
          </div>
          {isDesktop && (
            <div className="mb-[60px]">
              <Button
                className="w-[300px] mt-8 mx-auto"
                disabled={!isSubmitable}
                type="button"
                variant="primary"
                onClick={handleSubmit}
              >
                {textDefault.title}
              </Button>
            </div>
          )}

          <Modal
            bottomSheetClassName="px-[16px] pt-[20px] pb-[40px]"
            className="px-4 lg:px-11 py-10"
            closeOutside={false}
            controlledOnClose={() => null}
            isControlledOpen={showModal}
            render={() => {
              return (
                <div className="mx-[15px] mt-[10px]">
                  {<ModalUI />}
                  <div className="flex space-x-[10px] h-[50px]">
                    <Button
                      className="flex-1 px-0"
                      type="button"
                      variant="secondary"
                      onClick={() => {
                        setShowModal(false);
                        navigate(ROUTES.friendsTournament);
                      }}
                    >
                      Invitar más tarde
                    </Button>
                    <Button
                      className="flex-1 px-0"
                      type="button"
                      variant="primary"
                      onClick={() => {
                        setShowModal(false);
                        navigate(ROUTES.InviteToFriendsTournament);
                      }}
                    >
                      Aceptar
                    </Button>
                  </div>
                </div>
              );
            }}
            showCloseButton={false}
            size="xs"
            variant="controlled"
          />

          <Modal
            bottomSheetClassName="px-[16px] pt-[20px] pb-[40px]"
            className="px-[1.06rem] lg:px-11 py-10"
            controlledOnClose={() => setShowDesktopVideoModal(false)}
            isControlledOpen={showDesktopVideoModal}
            render={() => {
              return (
                <div>
                  <iframe
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    height="315"
                    referrerPolicy="strict-origin-when-cross-origin"
                    src="https://www.youtube.com/embed/imDCARfBWak?si=K56cW08Z5N-SYAI4"
                    width="100%"
                  />
                </div>
              );
            }}
            showCloseButton={true}
            size="xs"
            variant="controlled"
          />
        </div>
      )}
      mobile={{
        leftContent: {
          backAction: () => {
            navigate(ROUTES.friendsTournament);
          },
        },
        centerContent: { title: textDefault.title },
      }}
      sidebar={() =>
        isDesktop && (
          <Sidebar className="lg:w-[410px]">
            <div
              className={cn(
                'relative overflow-hidden rounded-md mt-[50px] bg-no-repeat bg-left',
                'bg-[url("/assets/images/bg_tda-creation.png")]  h-[369px] w-[426px]',
                'bg-[length:426px_369px]' // bg-origin-border bg-clip-border
              )}
            >
              <div className="pl-[38px] pt-[161px] relative">
                <div className="flex text-[31px]/[30px] font-normal font-bebas text-white justify-center mb-[0px] w-[230px]">
                  <span>COMO ADMINISTRADOR DE UN TORNEO:</span>
                </div>
                <ul>
                  <li className="my-[8px] text-[15px]">
                    <img className="inline" src={inviteTda} />{' '}
                    <span className="text-white ml-[5px]">
                      Invitá a quien quieras.
                    </span>
                  </li>
                  <li className="my-[8px] text-[15px]">
                    <img className="inline" src={acceptTda} />{' '}
                    <span className="text-white ml-[5px]">
                      Aceptá a quien se postule en tu torneo.
                    </span>
                  </li>
                  <li className="my-[8px] text-[15px]">
                    <img className="inline" src={photoTda} />{' '}
                    <span className="text-white ml-[5px]">
                      Subí una foto que identifique tu torneo.
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </Sidebar>
        )
      }
      withBanners={false}
    />
  );
};
